import type { LocalDate } from '../time';
import { GENERAL_DIMENSION, type ProgressionEvent } from './compute';
import { MAX_AREAS_EXTRA, POINTS, TASKS_PER_DAY_CAP } from './rules';

/**
 * DE LO QUE EL USUARIO HACE A PUNTOS.
 *
 * Esta capa existe para que **haya un solo sitio** donde se decide qué puntúa.
 * La alternativa —repartir `+10` por las pantallas— garantiza que un día el
 * cliente y el servidor dejen de estar de acuerdo, y el usuario vea el número
 * cambiar al sincronizar.
 *
 * El id de cada evento es determinista y se deriva del hecho que lo produjo, no
 * de un contador: así, aplicar dos veces la misma entrada de hábito no da
 * puntos dos veces (P3), sin necesidad de llevar una lista de lo ya contado.
 */

export type HabitAward = {
  readonly entryId: string;
  readonly localDate: LocalDate;
  /** Área de vida del hábito. */
  readonly dimension: string;
  readonly status: 'done' | 'skipped' | 'pending';
  /** Valor registrado en un hábito cuantificable. */
  readonly value?: number | undefined;
  readonly target?: number | undefined;
  readonly attested: boolean;
};

export function awardForHabit(entry: HabitAward): ProgressionEvent | null {
  // Omitir a propósito vale cero. Cero, no negativo: la app no castiga una
  // decisión consciente; solo deja de premiarla.
  if (entry.status !== 'done') return null;

  const overTarget =
    entry.value !== undefined && entry.target !== undefined && entry.value > entry.target;

  return {
    id: `habit:${entry.entryId}`,
    localDate: entry.localDate,
    dimension: entry.dimension,
    points: POINTS.habitDone + (overTarget ? POINTS.habitOverTarget : 0),
    attested: entry.attested,
  };
}

/**
 * EL HÁBITO QUE TRABAJA VARIAS ÁREAS (reglamento v4). El área principal recibe lo de
 * siempre —el mismo evento, con el mismo id, así que el historial de un hábito de un
 * área recalcula idéntico— y cada área extra `POINTS.habitExtraArea` más. Como
 * mucho `MAX_AREAS_EXTRA`, sin repetir la principal ni repetirse, en el orden dado.
 */
export function awardsForHabit(
  entry: HabitAward,
  extraAreas: readonly string[],
): ProgressionEvent[] {
  const principal = awardForHabit(entry);
  if (!principal) return [];
  const extras = [...new Set(extraAreas)]
    .filter((a) => a !== entry.dimension)
    .slice(0, MAX_AREAS_EXTRA);
  return [
    principal,
    ...extras.map((dimension) => ({
      id: `${principal.id}:extra:${dimension}`,
      localDate: entry.localDate,
      dimension,
      points: POINTS.habitExtraArea,
      attested: entry.attested,
    })),
  ];
}

export type FocusAward = {
  readonly sessionId: string;
  readonly localDate: LocalDate;
  readonly dimension: string;
  readonly actualMinutes: number;
  readonly interrupted: boolean;
  readonly attested: boolean;
};

/**
 * Una sesión interrumpida vale 0, y eso es deliberado: si valiera algo, la
 * forma óptima de subir sería arrancar sesiones y cortarlas. Pero tampoco resta
 * — empezar y no poder terminar no es un fallo moral.
 *
 * El umbral de 45 minutos para la tarifa larga, y no 50 exactos, es porque nadie
 * cierra el temporizador en el segundo justo.
 */
export function awardForFocus(session: FocusAward): ProgressionEvent | null {
  if (session.interrupted) return null;
  if (session.actualMinutes < 10) return null;

  return {
    id: `focus:${session.sessionId}`,
    localDate: session.localDate,
    dimension: session.dimension,
    points: session.actualMinutes >= 45 ? POINTS.focus50 : POINTS.focus25,
    attested: session.attested,
  };
}

export function awardForJournal(
  entryId: string,
  localDate: LocalDate,
  attested: boolean,
): ProgressionEvent {
  return {
    id: `journal:${entryId}`,
    localDate,
    dimension: GENERAL_DIMENSION,
    points: POINTS.journalEntry,
    attested,
  };
}

export function awardForMood(
  logId: string,
  localDate: LocalDate,
  attested: boolean,
): ProgressionEvent {
  return {
    id: `mood:${logId}`,
    localDate,
    dimension: GENERAL_DIMENSION,
    points: POINTS.moodLog,
    attested,
  };
}

export function awardForGoalMilestone(
  milestoneId: string,
  localDate: LocalDate,
  dimension: string,
  attested: boolean,
): ProgressionEvent {
  return {
    id: `goalstep:${milestoneId}`,
    localDate,
    dimension,
    points: POINTS.goalMilestone,
    attested,
  };
}

export function awardForGoalCompleted(
  goalId: string,
  localDate: LocalDate,
  dimension: string,
  attested: boolean,
): ProgressionEvent {
  return {
    id: `goal:${goalId}`,
    localDate,
    dimension,
    points: POINTS.goalCompleted,
    attested,
  };
}

export function awardForDreamCompleted(
  dreamId: string,
  localDate: LocalDate,
  dimension: string,
  attested: boolean,
): ProgressionEvent {
  return {
    id: `dream:${dreamId}`,
    localDate,
    dimension,
    points: POINTS.dreamCompleted,
    attested,
  };
}

/**
 * La revisión reparte sus puntos **entre las dimensiones activas**, no los da a
 * `general`. Revisar la semana es trabajo sobre las áreas que uno tiene, y el
 * reparto hace que el número aparezca donde el usuario lo va a buscar.
 *
 * El resto de la división entera se le da a la primera por orden alfabético: hay
 * que dárselo a alguien y tiene que ser siempre el mismo, o el resultado
 * dependería del orden de llegada.
 */
export function awardsForReview(
  reviewId: string,
  localDate: LocalDate,
  period: 'weekly' | 'monthly',
  dimensions: readonly string[],
  attested: boolean,
): ProgressionEvent[] {
  const total = period === 'weekly' ? POINTS.weeklyReview : POINTS.monthlyReview;
  return repartir(`review:${reviewId}`, total, localDate, dimensions, attested);
}

/**
 * LA RUEDA DE LA VIDA DEL MES, sellada (reglamento v3). Una por mes: el id sale del
 * mes, así que sellarla otra vez o desde otro móvil no suma dos veces. Se reparte
 * como la revisión, entre las áreas activas.
 */
export function awardsForWheel(
  monthKey: string,
  localDate: LocalDate,
  dimensions: readonly string[],
  attested: boolean,
): ProgressionEvent[] {
  return repartir(`wheel:${monthKey}`, POINTS.lifeWheel, localDate, dimensions, attested);
}

function repartir(
  idBase: string,
  total: number,
  localDate: LocalDate,
  dimensions: readonly string[],
  attested: boolean,
): ProgressionEvent[] {
  const active = [...new Set(dimensions)].sort();

  if (active.length === 0) {
    return [{ id: idBase, localDate, dimension: GENERAL_DIMENSION, points: total, attested }];
  }

  const each = Math.floor(total / active.length);
  const remainder = total - each * active.length;

  return active.map((dimension, index) => ({
    id: `${idBase}:${dimension}`,
    localDate,
    dimension,
    points: each + (index === 0 ? remainder : 0),
    attested,
  }));
}

export type PlanAward = {
  readonly localDate: LocalDate;
  readonly pillarAreaId: string | null;
  readonly frogTaskId: string | null;
  readonly ritualSealed: boolean;
  readonly attested: boolean;
};

export type TaskAward = {
  readonly taskId: string;
  readonly plannedDate: LocalDate | null;
  readonly areaId: string | null;
  readonly doneLocalDate: LocalDate | null;
  readonly attested: boolean;
};

/**
 * EL RITUAL, LO DIFÍCIL PRIMERO Y LAS TAREAS, en un solo sitio para cliente y
 * servidor.
 *
 * Lo difícil primero solo lo es si la tarea sigue planificada para el día del plan:
 * moverla a otro día la convierte en una tarea más, y así no hay dos «difíciles»
 * compitiendo por la misma tarea. Puntúa el día en que se hizo, no el planificado.
 *
 * Las tareas normales puntúan como mucho cinco por día de hecho, y las cinco se
 * eligen por id, nunca por orden de llegada: dos móviles que sincronizan en distinto
 * orden tienen que ver el mismo número (P2/P5).
 */
export function awardsForIntention(
  plans: readonly PlanAward[],
  tasks: readonly TaskAward[],
): ProgressionEvent[] {
  const events: ProgressionEvent[] = [];
  const byId = new Map(tasks.map((t) => [t.taskId, t]));
  const frogs = new Set<string>();

  for (const plan of plans) {
    if (plan.ritualSealed) {
      events.push({
        id: `ritual:${plan.localDate}`,
        localDate: plan.localDate,
        dimension: plan.pillarAreaId ?? GENERAL_DIMENSION,
        points: POINTS.ritualSealed,
        attested: plan.attested,
      });
    }
    const frog = plan.frogTaskId ? byId.get(plan.frogTaskId) : undefined;
    if (!frog || frog.plannedDate !== plan.localDate) continue;
    frogs.add(frog.taskId);
    if (frog.doneLocalDate === null) continue;
    events.push({
      id: `frog:${plan.localDate}`,
      localDate: frog.doneLocalDate,
      dimension: frog.areaId ?? GENERAL_DIMENSION,
      points: POINTS.frogDone,
      attested: frog.attested,
    });
  }

  const byDay = new Map<LocalDate, TaskAward[]>();
  for (const task of tasks) {
    if (task.doneLocalDate === null || frogs.has(task.taskId)) continue;
    const list = byDay.get(task.doneLocalDate) ?? [];
    list.push(task);
    byDay.set(task.doneLocalDate, list);
  }
  for (const [day, list] of byDay) {
    list.sort((a, b) => (a.taskId < b.taskId ? -1 : a.taskId > b.taskId ? 1 : 0));
    for (const task of list.slice(0, TASKS_PER_DAY_CAP)) {
      events.push({
        id: `task:${task.taskId}`,
        localDate: day,
        dimension: task.areaId ?? GENERAL_DIMENSION,
        points: POINTS.taskDone,
        attested: task.attested,
      });
    }
  }

  return events;
}
